import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plug, Save } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { ConnectionInput, DatabaseType } from '../api/client';
import { Alert } from '../components/Feedback';

interface Option {
  value: DatabaseType;
  label: string;
  blurb: string;
  defaultPort?: number;
  networked: boolean;
  ssl?: boolean;
}

const engines: Option[] = [
  {
    value: 'postgresql',
    label: 'PostgreSQL',
    blurb: 'PostgreSQL 12 or newer',
    defaultPort: 5432,
    networked: true,
    ssl: true,
  },
  {
    value: 'mysql',
    label: 'MySQL',
    blurb: 'MySQL 8 or newer',
    defaultPort: 3306,
    networked: true,
    ssl: true,
  },
  {
    value: 'mariadb',
    label: 'MariaDB',
    blurb: 'MariaDB 10.6 or newer',
    defaultPort: 3306,
    networked: true,
    ssl: true,
  },
  {
    value: 'sqlserver',
    label: 'SQL Server',
    blurb: 'SQL Server 2019 or newer',
    defaultPort: 1433,
    networked: true,
    ssl: true,
  },
  {
    value: 'sqlite',
    label: 'SQLite',
    blurb: 'A local file path',
    networked: false,
  },
];

const sslModes = ['disable', 'allow', 'prefer', 'require', 'verify-ca', 'verify-full'];

/** Placeholders shown under the URL box, one per engine, so the expected shape
 *  is unambiguous without the user having to guess the scheme. */
const urlExamples: Record<DatabaseType, string> = {
  postgresql: 'postgresql://user:password@host:5432/database?sslmode=verify-full',
  mysql: 'mysql://user:password@host:3306/database',
  mariadb: 'mariadb://user:password@host:3306/database',
  sqlserver: 'sqlserver://user:password@host:1433/database?encrypt=true',
  sqlite: 'sqlite:////var/lib/data/analytics.db',
};

export function NewDatabasePage() {
  const navigate = useNavigate();

  const [mode, setMode] = useState<'fields' | 'url'>('fields');
  const [engine, setEngine] = useState<Option>(engines[0]);
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(String(engines[0].defaultPort ?? ''));
  const [databaseName, setDatabaseName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sslMode, setSslMode] = useState('prefer');
  const [connectionUrl, setConnectionUrl] = useState('');

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(
    null,
  );

  // In URL mode the engine is read from the scheme, so it is not sent; the
  // server derives it. Only one of the two forms is ever populated, which is
  // what the API requires.
  const payload = (): ConnectionInput =>
    mode === 'url'
      ? { name: name.trim(), connection_url: connectionUrl.trim() }
      : {
          name: name.trim(),
          database_type: engine.value,
          database_name: databaseName.trim(),
          ...(engine.networked
            ? {
                host: host.trim(),
                port: port ? Number(port) : undefined,
                username: username.trim(),
                password: password || undefined,
                ssl_mode: sslMode,
              }
            : {}),
        };

  const selectEngine = (option: Option) => {
    setEngine(option);
    setPort(String(option.defaultPort ?? ''));
    setFeedback(null);
  };

  /** Test the details before saving, so a typo is caught while it is visible. */
  const test = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const result = await api.databases.test(payload());
      setFeedback({
        tone: result.success ? 'success' : 'error',
        // In URL mode the server's message is used verbatim: it echoes back how
        // the string was read, which is the only way to see a misparsed host.
        text:
          result.success && mode === 'url'
            ? result.message
            : result.success
              ? `Connected successfully${result.latency_ms ? ` in ${result.latency_ms} ms` : ''}.`
              : result.message,
      });
    } catch (error) {
      setFeedback({
        tone: 'error',
        text: error instanceof ApiError ? error.message : 'The test could not be run.',
      });
    } finally {
      setTesting(false);
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      await api.databases.create(payload());
      navigate('/databases');
    } catch (error) {
      setFeedback({
        tone: 'error',
        text: error instanceof ApiError ? error.message : 'The database could not be saved.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page narrow">
      <button className="link back" onClick={() => navigate('/databases')}>
        <ArrowLeft size={16} /> Databases
      </button>

      <header className="page-head">
        <div>
          <p className="eyebrow">NEW CONNECTION</p>
          <h1>Add a database</h1>
          <p className="muted">
            The connection is tested before it is saved, and the password is encrypted at rest.
          </p>
        </div>
      </header>

      <form className="panel form" onSubmit={save}>
        <div className="field">
          <label>How should the details be entered?</label>
          <div className="engine-grid two">
            <button
              type="button"
              className={mode === 'fields' ? 'engine selected' : 'engine'}
              onClick={() => {
                setMode('fields');
                setFeedback(null);
              }}
            >
              <strong>Enter fields</strong>
              <small>Host, port, and credentials one by one</small>
            </button>
            <button
              type="button"
              className={mode === 'url' ? 'engine selected' : 'engine'}
              onClick={() => {
                setMode('url');
                setFeedback(null);
              }}
            >
              <strong>Paste a connection string</strong>
              <small>One URL, the way your provider gives it to you</small>
            </button>
          </div>
        </div>

        <div className="field">
          <label htmlFor="name">Display name</label>
          <input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            placeholder="Production analytics"
          />
          <small className="muted">Only you see this label.</small>
        </div>

        {mode === 'url' ? (
          <>
            <div className="field">
              <label htmlFor="url">Connection string</label>
              <input
                id="url"
                className="sql"
                value={connectionUrl}
                onChange={(e) => setConnectionUrl(e.target.value)}
                required
                maxLength={4096}
                autoComplete="off"
                spellCheck={false}
                placeholder={urlExamples[engine.value]}
              />
              <small className="muted">
                Use <code>{urlExamples[engine.value]}</code> as the shape. The engine comes
                from the scheme, and a driver suffix such as{' '}
                <code>+asyncpg</code> is accepted and ignored.
              </small>
            </div>
            <p className="notice">
              The password in the string is extracted, encrypted at rest, and never shown
              again. Testing a string reports how it was read, with the password replaced.
            </p>
          </>
        ) : (
          <>
            <div className="field">
              <label>Database engine</label>
              <div className="engine-grid">
                {engines.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={engine.value === option.value ? 'engine selected' : 'engine'}
                    onClick={() => selectEngine(option)}
                  >
                    <strong>{option.label}</strong>
                    <small>{option.blurb}</small>
                  </button>
                ))}
              </div>
            </div>

            {engine.networked ? (
              <>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="host">Host</label>
                    <input
                      id="host"
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                      required
                      placeholder="db.internal.example.com"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="port">Port</label>
                    <input
                      id="port"
                      type="number"
                      min={1}
                      max={65535}
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                      placeholder={String(engine.defaultPort ?? '')}
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="database">Database name</label>
                  <input
                    id="database"
                    value={databaseName}
                    onChange={(e) => setDatabaseName(e.target.value)}
                    required
                    placeholder="analytics"
                  />
                </div>

                <div className="field-row">
                  <div className="field">
                    <label htmlFor="username">Username</label>
                    <input
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="password">Password</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                {engine.ssl && (
                  <div className="field">
                    <label htmlFor="ssl">TLS mode</label>
                    <select
                      id="ssl"
                      value={sslMode}
                      onChange={(e) => setSslMode(e.target.value)}
                    >
                      {sslModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                    <small className="muted">
                      Use <code>verify-full</code> unless the server uses a private certificate
                      authority.
                    </small>
                  </div>
                )}
              </>
            ) : (
              <div className="field">
                <label htmlFor="database">File path</label>
                <input
                  id="database"
                  value={databaseName}
                  onChange={(e) => setDatabaseName(e.target.value)}
                  required
                  placeholder="/var/lib/data/analytics.db"
                />
                <small className="muted">
                  The path as seen from the MCPForge API container, not from your machine. It is
                  opened read-only.
                </small>
              </div>
            )}
          </>
        )}

        <p className="notice">
          MCPForge reads through a dedicated account where you can. A read-only PostgreSQL role
          is the strongest control, since the database refuses writes even if the query policy
          were bypassed.
        </p>

        {feedback && <Alert tone={feedback.tone}>{feedback.text}</Alert>}

        <footer className="form-actions">
          <button type="button" className="secondary" onClick={test} disabled={testing}>
            <Plug size={16} /> {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button className="primary" disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save database'}
          </button>
        </footer>
      </form>
    </div>
  );
}
